import { gunzipSync, unzipSync } from "fflate";
import { TptRow } from "./fundParseTptTypes";

function decodeBase64Payload(encodedPayload: string): Uint8Array {
    const binary = atob(encodedPayload);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
}

function isZipArchive(bytes: Uint8Array): boolean {
    return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b;
}

function unzipPreferredJsonMember(bytes: Uint8Array): Uint8Array {
    const entries = unzipSync(bytes);
    const memberNames = Object.keys(entries).filter((name) => !name.endsWith("/"));
    if (!memberNames.length) {
        throw new Error("ZIP payload is empty.");
    }
    const preferredMember = memberNames.find((name) => name.toLowerCase().endsWith(".json")) || memberNames[0];
    return entries[preferredMember];
}

function tryGunzip(bytes: Uint8Array): Uint8Array | null {
    if (bytes.length < 2 || bytes[0] !== 0x1f || bytes[1] !== 0x8b) {
        return null;
    }
    try {
        return gunzipSync(bytes);
    } catch {
        return null;
    }
}

function coerceTptRecords(payload: unknown): TptRow[] {
    if (!Array.isArray(payload)) {
        throw new TypeError(`TPT report JSON must be a list of records, got ${typeof payload}.`);
    }
    return payload.map((item, index) => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            throw new TypeError(`TPT report record at index ${index} must be an object.`);
        }
        return item as TptRow;
    });
}

export function reportPayloadToTptRecords(report: {
    data?: unknown;
    base64?: string | null;
}): TptRow[] {
    const reportBase64 = report.base64;
    if (typeof reportBase64 === "string" && reportBase64.trim()) {
        let payloadBytes = decodeBase64Payload(reportBase64);
        if (isZipArchive(payloadBytes)) {
            payloadBytes = unzipPreferredJsonMember(payloadBytes);
        } else {
            const gunzipped = tryGunzip(payloadBytes);
            if (gunzipped) {
                payloadBytes = gunzipped;
            }
        }
        const jsonText = new TextDecoder().decode(payloadBytes);
        return coerceTptRecords(JSON.parse(jsonText));
    }

    const reportData = report.data;
    if (typeof reportData === "string") {
        return coerceTptRecords(JSON.parse(reportData));
    }
    if (reportData !== null && typeof reportData !== "undefined") {
        return coerceTptRecords(reportData);
    }

    throw new Error("Report payload has neither non-empty base64 nor usable data.");
}

export function normalizeIdentifier(value: unknown): string {
    if (value === null || typeof value === "undefined") {
        return "";
    }
    const normalized = String(value).replace(/\s/g, "").trim().toUpperCase();
    if (!normalized || ["NAN", "NAT", "NONE"].includes(normalized)) {
        return "";
    }
    return normalized;
}
